import { AfterViewInit, Component, computed, ElementRef, inject, OnInit, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonButtonRowWidget, CommonSelectFieldWidget, CommonTextFieldWidget, UtilityService } from '@eforall/common';
import { DashboardDescriptiveSelectFieldWidget, DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { DropoutUpsertValues } from '@interfaces';
import { AppService } from '../../../../services';
import { DropoutDialogDataService } from './dropout-dialog-data.service';
import { DropoutDialogForm, DropoutFormFields, DropoutTargetType } from './dropout-dialog.form';

export interface DropoutDialogData {
	dropoutTargetType: DropoutTargetType,
	dropoutId?: number,
}
@Component({
	selector: 'dropout-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		DashboardDescriptiveSelectFieldWidget,
		CommonSelectFieldWidget,
		CommonTextFieldWidget,
		CommonButtonRowWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './dropout.dialog.html'
})

export class DropoutDialog implements OnInit, AfterViewInit {

	public dialogRef = inject(MatDialogRef<DropoutDialog>);
	public dialogContext: DialogContext<DropoutDialogData> = inject(MAT_DIALOG_DATA);

	public dialogDataService = inject(DropoutDialogDataService);
	private util = inject(UtilityService);
	private app = inject(AppService);

	public readonly dropoutForm = new DropoutDialogForm(this.dialogContext.data?.dropoutTargetType);
	public saveBtn = viewChild.required<ElementRef<HTMLButtonElement>>('formSaveBtn');


	public dropoutDetails = computed(() => {
		const data = this.dialogDataService.dialogData();
		if (!data) return undefined;

		return {
			...data,
			createdByStaff: this.app.getStaffNameById(data.createdByStaffId),
			formattedCreatedDate: this.util.date.formatUTC(data.createdUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US'),
			updatedByStaff: this.app.getStaffNameById(data.updatedByStaffId),
			formattedUpdatedDate: this.util.date.formatUTC(data.updatedUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US')
		}
	});

	public header = computed(() => {
		const data = this.dialogContext.data;
		if (!data) return 'Drop-out';

		return data.dropoutId ? `Edit ${data.dropoutTargetType} Drop-out` : `Drop-out ${data.dropoutTargetType}`;
	});


	async ngOnInit() {
		this.dialogDataService.resetDialogData();
		const data = this.dialogContext.data;
		if (data?.dropoutId) {
			await this.dialogDataService.getDropoutDetails(data.dropoutId);
		}
	}


	ngAfterViewInit() {
		this.dropoutForm.form.setSubmitButton(this.saveBtn(), async (values: DropoutFormFields, changed: string[]) => {

			const data = this.dialogContext.data;
			if (changed.length === 0) return;

			// creating a dialogAction to pass back the result to parent component
			const dialogAction: DialogAction = {
				id: 'submit',
				cssClass: signal(undefined),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Submit'),
				linkType: "no-action",
				willCloseDialog: true,
				callbackResult: <DropoutUpsertValues>{
					dropoutWeek: values.dropoutWeek ? Number(values.dropoutWeek) : undefined,
					dropoutReasonId: values.dropoutReasonId,
					notes: values.notes,
				}
			};

			this.dialogRef.close(dialogAction);
		});
	}

	resetForm() {
		this.dropoutForm.form.resetAll();
	}

}